import { motion } from "framer-motion";
import { ArrowUpRight, ShieldCheck, Sparkles, Star } from "lucide-react";
import { CLINIC } from "@/data/content";

// Kinetic underline block (word-by-word rise)
const wordAnim = (i) => ({
  hidden: { y: 60, opacity: 0 },
  show:   { y: 0,  opacity: 1, transition: { duration: 0.9, ease: [0.2, 0.7, 0.2, 1], delay: 0.25 + i * 0.08 } },
});

export default function Hero() {
  const headline = ["Gentle,", "modern", "dentistry", "in", "the", "heart", "of", "Andheri", "East."];

  return (
    <section id="top" className="relative pt-[110px] md:pt-[170px] pb-14 md:pb-24 soft-radial overflow-hidden" data-testid="hero-section">
      {/* decorative twinkles */}
      <span className="twinkle absolute top-32 left-4 md:left-16 text-3xl hidden sm:inline-block">
        <TwinkleSvg />
      </span>
      <span className="twinkle absolute bottom-16 right-4 md:right-32 text-2xl hidden sm:inline-block" style={{ animationDirection: "reverse" }}>
        <TwinkleSvg />
      </span>

      <div className="max-w-[1440px] mx-auto px-5 md:px-12">
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-center">
          {/* LEFT — headline + CTA */}
          <div className="col-span-12 md:col-span-7">
            <motion.div className="eyebrow mb-4 md:mb-6 text-[10px] md:text-[12px]" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
              Best dentist in Andheri East · Powai
            </motion.div>

            <h1 className="font-display text-[13vw] md:text-[6.5vw] lg:text-[5.4vw] leading-[0.98] tracking-[-0.03em] text-[hsl(var(--ink))]">
              {headline.map((w, i) => (
                <span key={i} className="inline-block overflow-hidden mr-[0.22em]">
                  <motion.span variants={wordAnim(i)} initial="hidden" animate="show" className="inline-block">
                    {w === "modern" ? <span className="hl text-[hsl(var(--primary))]">{w}</span> : w}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              className="mt-6 md:mt-8 max-w-lg text-[hsl(var(--ink-soft))] text-[15px] md:text-[17px] leading-relaxed"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.7 }}
            >
              Painless checkups, root canals, aligners and smile design with <span className="text-[hsl(var(--ink))] font-semibold">{CLINIC.doctor}</span> — a calmer chair for anxious, busy and everyday patients alike.
            </motion.p>

            <motion.div
              className="mt-6 md:mt-8 flex flex-wrap items-center gap-3"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.7 }}
            >
              <a href="#book" className="btn-pill btn-solid" data-testid="hero-book-cta">
                Book appointment
                <span className="arrow"><ArrowUpRight size={16} strokeWidth={2} /></span>
              </a>
              <a href="#services" className="btn-pill btn-outline" data-testid="hero-services-cta">
                Our services
                <span className="arrow"><ArrowUpRight size={16} strokeWidth={2} /></span>
              </a>
            </motion.div>

            {/* Trust chips */}
            <motion.div
              className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}
            >
              <Chip icon={<Star size={14} className="fill-current" />}>4.9★ Google rating</Chip>
              <Chip icon={<ShieldCheck size={14} />}>Sterilised, safe protocols</Chip>
              <Chip icon={<Sparkles size={14} />}>15+ years of gentle care</Chip>
            </motion.div>
          </div>

          {/* RIGHT — Doctor portrait in shaped frame */}
          <div className="col-span-12 md:col-span-5">
            <div className="relative mx-auto max-w-[360px] md:max-w-[460px]">
              {/* Blob background */}
              <motion.div
                className="absolute inset-0 -z-10"
                initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1.2, delay: 0.3 }}
              >
                <div className="absolute inset-6 rounded-full bg-[hsl(var(--primary)/0.20)] blur-2xl" />
                <div className="absolute inset-10 rounded-full bg-[hsl(var(--primary)/0.30)]" />
              </motion.div>

              <motion.figure
                className="relative rounded-[160px_160px_20px_20px] md:rounded-[220px_220px_28px_28px] overflow-hidden bg-[hsl(var(--primary-light))]"
                initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1, delay: 0.4, ease: [0.2,0.7,0.2,1] }}
              >
                <img
                  src="/assets/dr-raksha.jpeg"
                  alt="Dr Raksha Shetty — Best dentist in Andheri East & Powai"
                  className="w-full h-[420px] md:h-[600px] object-cover object-center"
                  loading="eager"
                />
              </motion.figure>

              {/* Floating rating card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4, duration: 0.7 }}
                className="absolute left-2 md:-left-10 bottom-10 md:bottom-16 bg-white rounded-2xl shadow-[0_20px_50px_-20px_rgba(46,36,27,0.35)] px-4 py-3 md:px-5 md:py-4 flex items-center gap-2.5 md:gap-3"
                data-testid="hero-rating-card"
              >
                <div className="text-[hsl(var(--primary))] flex">
                  {[0,1,2,3,4].map(i => <Star key={i} size={14} className="fill-current"/>)}
                </div>
                <div>
                  <div className="font-display text-base md:text-lg leading-none">4.9<span className="text-[hsl(var(--primary))]">/5</span></div>
                  <div className="text-[10px] md:text-[11px] text-[hsl(var(--ink-soft))] mt-1">100+ happy patients</div>
                </div>
              </motion.div>

              {/* Floating stat card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.6, duration: 0.7 }}
                className="absolute right-2 md:-right-10 top-8 md:top-16 bg-white rounded-2xl shadow-[0_20px_50px_-20px_rgba(46,36,27,0.35)] px-4 py-3 md:px-5 md:py-4"
                data-testid="hero-stat-card"
              >
                <div className="font-display text-xl md:text-2xl leading-none text-[hsl(var(--primary))]">15<span className="text-[hsl(var(--ink))]">+</span></div>
                <div className="text-[10px] md:text-[11px] text-[hsl(var(--ink-soft))] mt-1 max-w-[95px] md:max-w-[110px]">Years of gentle practice</div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Chip({ icon, children }) {
  return (
    <div className="flex items-center gap-2 text-[13px] text-[hsl(var(--ink-soft))] font-medium">
      <span className="w-8 h-8 rounded-full bg-[hsl(var(--primary-light))] text-[hsl(var(--primary-dark))] flex items-center justify-center">{icon}</span>
      {children}
    </div>
  );
}

function TwinkleSvg() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2 L13.5 10.5 L22 12 L13.5 13.5 L12 22 L10.5 13.5 L2 12 L10.5 10.5 Z" fill="currentColor" />
    </svg>
  );
}
